import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// ── Sample report data used across all preview charts ──────────────────────
const SAMPLE = {
    product:     'Sony WH-1000XM5 Wireless Headphones',
    retailer:    'Walmart',
    buyPrice:    88.05,
    shipping:    2.95,
    landedCost:  91.00,
    sellPrice:   159.99,
    referralFee: 24.00,
    fbaFee:      3.50,
    totalFees:   27.50,
    netProfit:   41.49,
    roi:         45.6,
    margin:      25.9,
    bsr:         4231,
    monthlySales:2847,
    competitors: 0,
};

const fmt = v => `$${v.toFixed(2)}`;
const ACCENT = '#e94560';
const PRIMARY = '#1a1a2e';
const GREEN  = '#27ae60';
const BLUE   = '#2980b9';
const PURPLE = '#8e44ad';

// ── Inline SVG Donut Pie Chart ─────────────────────────────────────────────
function PieChart() {
    const segs = [
        { label: 'Landed Cost', value: SAMPLE.landedCost, color: BLUE   },
        { label: 'Amazon Fees', value: SAMPLE.totalFees,  color: ACCENT  },
        { label: 'Net Profit',  value: SAMPLE.netProfit,  color: GREEN   },
    ];
    const total = segs.reduce((s, sg) => s + sg.value, 0);
    const cx = 70, cy = 70, r = 58, ir = 30;
    let cum = -90;
    const toRad = a => a * Math.PI / 180;
    const arcs = segs.map(sg => {
        const angle = (sg.value / total) * 360;
        const s = cum, e = cum + angle;
        cum += angle;
        const large = angle > 180 ? 1 : 0;
        const x1  = cx + r  * Math.cos(toRad(s)), y1  = cy + r  * Math.sin(toRad(s));
        const x2  = cx + r  * Math.cos(toRad(e)), y2  = cy + r  * Math.sin(toRad(e));
        const xi1 = cx + ir * Math.cos(toRad(s)), yi1 = cy + ir * Math.sin(toRad(s));
        const xi2 = cx + ir * Math.cos(toRad(e)), yi2 = cy + ir * Math.sin(toRad(e));
        return { ...sg, d: `M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2} L${xi2},${yi2} A${ir},${ir} 0 ${large} 0 ${xi1},${yi1} Z` };
    });
    return (
        <div className="flex items-center gap-5">
            <svg viewBox="0 0 140 140" width="120" height="120" className="flex-shrink-0">
                {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} />)}
                <text x="70" y="65" textAnchor="middle" fontSize="11" fontWeight="bold" fill={PRIMARY}>ROI</text>
                <text x="70" y="80" textAnchor="middle" fontSize="14" fontWeight="bold" fill={GREEN}>{SAMPLE.roi}%</text>
            </svg>
            <div className="space-y-2">
                {arcs.map((a, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: a.color }}></div>
                        <div>
                            <div className="text-xs text-gray-500">{a.label}</div>
                            <div className="text-sm font-bold" style={{ color: a.color }}>{fmt(a.value)}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Inline SVG Bar Chart ───────────────────────────────────────────────────
function BarChart() {
    const bars = [
        { label: 'Buy Price', value: SAMPLE.buyPrice,   color: BLUE   },
        { label: 'Landed',    value: SAMPLE.landedCost, color: PURPLE  },
        { label: 'Fees',      value: SAMPLE.totalFees,  color: ACCENT  },
        { label: 'You Sell',  value: SAMPLE.sellPrice,  color: GREEN   },
    ];
    const max = Math.max(...bars.map(b => b.value));
    const W = 240, H = 110, bw = 44, gap = 12;
    return (
        <svg viewBox={`0 0 ${W} ${H + 28}`} width="100%" height={H + 28} style={{ maxWidth: 260 }}>
            {bars.map((b, i) => {
                const bh = (b.value / max) * H;
                const x  = i * (bw + gap) + 8;
                const y  = H - bh;
                return (
                    <g key={i}>
                        <rect x={x} y={y} width={bw} height={bh} fill={b.color} rx={5} opacity={0.92} />
                        <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize="9" fill="#333" fontWeight="bold">
                            ${b.value.toFixed(0)}
                        </text>
                        <text x={x + bw / 2} y={H + 14} textAnchor="middle" fontSize="8" fill="#888">{b.label}</text>
                    </g>
                );
            })}
        </svg>
    );
}

// ── Inline SVG Price History Line Chart ────────────────────────────────────
function LineChart() {
    const base = SAMPLE.sellPrice;
    const pts = [148, 152, 157, 124, 121, 155, 161, 159, 154, 158, 162, 160];
    const min = Math.min(...pts) * 0.93;
    const max = Math.max(...pts) * 1.04;
    const W = 400, H = 70;
    const sx = i => i * (W / (pts.length - 1));
    const sy = v => H - ((v - min) / (max - min)) * H;
    const pathD = pts.map((v, i) => `${i === 0 ? 'M' : 'L'}${sx(i)},${sy(v)}`).join(' ');
    const fillD = `M${sx(0)},${H} ` + pts.map((v, i) => `L${sx(i)},${sy(v)}`).join(' ') + ` L${sx(pts.length - 1)},${H} Z`;
    const buyY = sy(SAMPLE.buyPrice);
    const months = ['Jan','','Mar','','May','','Jul','','Sep','','Nov',''];
    return (
        <svg viewBox={`0 0 ${W} ${H + 20}`} width="100%" height={H + 20}>
            <defs>
                <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BLUE} stopOpacity="0.25" />
                    <stop offset="100%" stopColor={BLUE} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={fillD} fill="url(#lg)" />
            <path d={pathD} fill="none" stroke={BLUE} strokeWidth="2" strokeLinejoin="round" />
            {pts.map((v, i) => <circle key={i} cx={sx(i)} cy={sy(v)} r="3" fill={BLUE} />)}
            <line x1={0} y1={buyY} x2={W} y2={buyY} stroke={GREEN} strokeWidth="1.5" strokeDasharray="6,4" />
            <text x={W - 4} y={buyY - 4} textAnchor="end" fontSize="8" fill={GREEN} fontWeight="bold">Buy ${SAMPLE.buyPrice}</text>
            {months.map((m, i) => m && (
                <text key={i} x={sx(i)} y={H + 14} textAnchor="middle" fontSize="8" fill="#aaa">{m}</text>
            ))}
        </svg>
    );
}

// ── Urgency countdown (hours:mins since day start) ─────────────────────────
function Urgency() {
    const [t, setT] = useState('');
    useEffect(() => {
        const tick = () => {
            const now = new Date();
            const end = new Date(); end.setHours(23, 59, 59, 0);
            const diff = end - now;
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setT(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);
    return <span className="font-mono text-white font-extrabold text-lg tracking-widest">{t}</span>;
}

export default function Home() {
    return (
        <div>
            {/* ── HERO ──────────────────────────────────────────────────── */}
            <section className="relative overflow-hidden py-20 px-4"
                style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#0f3460 60%,#1a3a6e 100%)' }}>
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at 75% 50%,rgba(233,69,96,.18) 0%,transparent 60%)' }}/>
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6"
                                style={{ background:'rgba(233,69,96,.2)', border:'1px solid rgba(233,69,96,.4)', color:'#ff9fba' }}>
                                <i className="fas fa-lock text-xs"></i> Exclusive Out-of-Stock Intelligence
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-5" style={{ letterSpacing:'-0.5px' }}>
                                Be The <span style={{ color:'#e94560' }}>Only Seller</span><br />
                                — Zero Competition,<br />
                                Maximum Profit
                            </h1>
                            <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-lg">
                                Our AI hunts products that are <strong className="text-white">sold out at major retailers</strong> but still sellable on Amazon. You source before anyone else, list exclusively, and <strong className="text-white">set your own price</strong>.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/register"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90"
                                    style={{ background:'#e94560' }}>
                                    <i className="fas fa-rocket"></i> Get My Exclusive Report
                                </Link>
                                <Link to="/pricing"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm text-white transition-all"
                                    style={{ background:'rgba(255,255,255,.1)', border:'1.5px solid rgba(255,255,255,.3)' }}>
                                    View Pricing
                                </Link>
                            </div>
                            <div className="flex gap-8 mt-10 flex-wrap">
                                {[
                                    ['0',    'Competitors\nOn Listing'],
                                    ['45%+', 'Average\nROI'],
                                    ['$5.99','Starting\nPrice'],
                                    ['4',    'Exclusive Deals\nPer Report'],
                                ].map(([val, lbl]) => (
                                    <div key={val} className="text-center">
                                        <div className="text-2xl font-extrabold" style={{ color:'#e94560' }}>{val}</div>
                                        <div className="text-xs text-gray-400 mt-1 whitespace-pre-line leading-tight">{lbl}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Hero card */}
                        <div className="lg:flex justify-center">
                            <div className="rounded-2xl p-6 max-w-sm w-full"
                                style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.12)', backdropFilter:'blur(10px)' }}>
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Your Exclusive Edge</span>
                                    <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white" style={{ background:'#27ae60' }}>
                                        <i className="fas fa-user-shield mr-1"></i>Only Seller
                                    </span>
                                </div>
                                <ul className="space-y-3 mb-5">
                                    {[
                                        ['fa-ban',       '#e94560', 'Product is OUT OF STOCK at Walmart / Target'],
                                        ['fa-crown',     '#f39c12', 'You buy the last clearance stock available'],
                                        ['fa-tag',       '#27ae60', 'List on Amazon — no competitors to undercut you'],
                                        ['fa-dollar-sign','#2980b9','Set your own high price & maximize profit'],
                                        ['fa-infinity',  '#8e44ad', 'Sell until stock runs out — 100% margin control'],
                                    ].map(([icon, color, text]) => (
                                        <li key={text} className="flex items-start gap-3 text-sm text-gray-300">
                                            <i className={`fas ${icon} mt-0.5 flex-shrink-0`} style={{ color }}></i>
                                            {text}
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/register"
                                    className="block text-center py-3 rounded-xl font-bold text-white text-sm hover:opacity-90 transition-all"
                                    style={{ background:'#e94560' }}>
                                    Start Now — Free Account
                                </Link>
                                <p className="text-center text-gray-500 text-xs mt-3">Starting from $5.99 · No subscription required</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── OUT-OF-STOCK ADVANTAGE ──────────────────────────────────── */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                            style={{ background:'rgba(233,69,96,.1)', color:'#e94560' }}>The Strategy</span>
                        <h2 className="text-3xl font-extrabold mb-3" style={{ color:PRIMARY }}>
                            Why "Out of Stock" Is a <span style={{ color:'#e94560' }}>Gold Mine</span>
                        </h2>
                        <div className="w-10 h-1 rounded mx-auto mb-4" style={{ background:'#e94560' }}></div>
                        <p className="text-gray-500 max-w-xl mx-auto">Most sellers fight over the same in-stock products. We find products sold out everywhere else — leaving you as the <strong>sole supplier on Amazon</strong>.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: 'fa-store-slash', color: ACCENT, bg: 'rgba(233,69,96,.08)',
                                step: '01',
                                title: 'Retailer Runs Out of Stock',
                                desc: 'Walmart, Target, Best Buy — all sell out of a hot product. Demand on Amazon stays sky-high while supply drops to near zero.',
                            },
                            {
                                icon: 'fa-box-open', color: '#f39c12', bg: 'rgba(243,156,18,.08)',
                                step: '02',
                                title: 'You Buy the Last Units',
                                desc: 'Our AI finds the clearance stock before it disappears. You purchase at the discounted clearance price — often 40–60% below Amazon value.',
                            },
                            {
                                icon: 'fa-crown', color: GREEN, bg: 'rgba(39,174,96,.08)',
                                step: '03',
                                title: 'You Control the Listing Price',
                                desc: 'With zero competition, you\'re the only seller. Set your price confidently above retail value. Buyers will pay — they have no other choice.',
                            },
                        ].map(card => (
                            <div key={card.step} className="rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: card.bg }}>
                                        <i className={`fas ${card.icon}`} style={{ color: card.color }}></i>
                                    </div>
                                    <span className="text-4xl font-black opacity-10" style={{ color: card.color }}>{card.step}</span>
                                </div>
                                <h5 className="font-bold text-gray-900 mb-2">{card.title}</h5>
                                <p className="text-gray-500 text-sm leading-relaxed">{card.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SAMPLE REPORT PREVIEW ───────────────────────────────────── */}
            <section className="py-16 px-4" style={{ background:'#f0f2f8' }}>
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                            style={{ background:'rgba(233,69,96,.1)', color:'#e94560' }}>Sample Report</span>
                        <h2 className="text-3xl font-extrabold mb-3" style={{ color:PRIMARY }}>
                            Here's Exactly What You Receive
                        </h2>
                        <div className="w-10 h-1 rounded mx-auto mb-4" style={{ background:'#e94560' }}></div>
                        <p className="text-gray-500 max-w-xl mx-auto">A professional PDF for every opportunity — complete with financial charts, direct purchase links, and your exclusive advantage explained.</p>
                    </div>

                    {/* Report mock-up card */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">

                        {/* Report header bar */}
                        <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3"
                            style={{ background: PRIMARY }}>
                            <div>
                                <div className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color:'#e94560' }}>
                                    OPPORTUNITY 1 OF 4
                                </div>
                                <div className="text-white font-bold text-base">{SAMPLE.product}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background:'rgba(39,174,96,.25)', color:'#4cde8f' }}>
                                    <i className="fas fa-user-shield mr-1"></i>EXCLUSIVE — 0 Competitors
                                </span>
                                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background:'rgba(233,69,96,.25)', color:'#ff9fba' }}>
                                    <i className="fas fa-ban mr-1"></i>OUT OF STOCK at Walmart
                                </span>
                            </div>
                        </div>

                        <div className="p-6 grid lg:grid-cols-2 gap-8">
                            {/* Left column */}
                            <div className="space-y-6">

                                {/* Financial table */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <i className="fas fa-chart-bar text-sm" style={{ color:'#e94560' }}></i>
                                        <span className="font-bold text-gray-900 text-sm uppercase tracking-wide">Financial Analysis (Per Unit)</span>
                                    </div>
                                    <div className="rounded-xl overflow-hidden border border-gray-100">
                                        {[
                                            ['Buy Price (Walmart — clearance)', fmt(SAMPLE.buyPrice),   false, '#111'],
                                            ['Shipping / FBA Prep',             fmt(SAMPLE.shipping),   true,  '#111'],
                                            ['Landed Cost',                      fmt(SAMPLE.landedCost), false, PRIMARY],
                                            ['Amazon Sell Price (you set this)', fmt(SAMPLE.sellPrice),  true,  BLUE],
                                            ['Amazon Referral Fee (15%)',         fmt(SAMPLE.referralFee),false, ACCENT],
                                            ['FBA Fulfillment Fee',               fmt(SAMPLE.fbaFee),     true,  ACCENT],
                                            ['Total Fees',                        fmt(SAMPLE.totalFees),  false, '#c0392b'],
                                            ['Net Profit',                        fmt(SAMPLE.netProfit),  true,  GREEN],
                                            ['ROI',                               `${SAMPLE.roi}%`,       false, GREEN],
                                        ].map(([label, value, shade, color], i) => (
                                            <div key={i} className="flex justify-between items-center px-3 py-2 text-sm"
                                                style={{ background: shade ? '#f9fafb' : 'white' }}>
                                                <span className="text-gray-600">{label}</span>
                                                <span className="font-bold" style={{ color }}>{value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Explanation block */}
                                <div className="rounded-xl p-4 border-l-4 border-green-400" style={{ background:'rgba(39,174,96,.06)' }}>
                                    <div className="font-bold text-sm mb-1" style={{ color: GREEN }}>
                                        <i className="fas fa-lightbulb mr-1"></i> Why This Works
                                    </div>
                                    <p className="text-gray-600 text-xs leading-relaxed">
                                        Walmart cleared this product below cost. Amazon buyers still need it — and you're the <strong>only one selling it</strong>.
                                        With 0 competitors, there's no price war. You keep 100% of the pricing power. At <strong>{SAMPLE.roi}% ROI</strong>, every $88 invested returns $41 in pure profit.
                                    </p>
                                </div>
                            </div>

                            {/* Right column */}
                            <div className="space-y-6">

                                {/* Pie chart */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <i className="fas fa-chart-pie text-sm" style={{ color:'#e94560' }}></i>
                                        <span className="font-bold text-gray-900 text-sm uppercase tracking-wide">Profit Breakdown</span>
                                    </div>
                                    <div className="rounded-xl border border-gray-100 p-4 bg-gray-50">
                                        <PieChart />
                                        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                                            The green slice is <strong>your profit</strong>. Because you're the only seller, this slice stays large — no competitor can chip away at it by undercutting your price.
                                        </p>
                                    </div>
                                </div>

                                {/* Bar chart */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <i className="fas fa-chart-bar text-sm" style={{ color:'#e94560' }}></i>
                                        <span className="font-bold text-gray-900 text-sm uppercase tracking-wide">Price Journey: Buy → Sell</span>
                                    </div>
                                    <div className="rounded-xl border border-gray-100 p-4 bg-gray-50">
                                        <BarChart />
                                        <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                                            The gap between <strong style={{ color:BLUE }}>what you pay</strong> and <strong style={{ color:GREEN }}>what you sell for</strong> is your profit window. Wider gap = more money in your pocket.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Price history full width */}
                        <div className="px-6 pb-6">
                            <div className="flex items-center gap-2 mb-3">
                                <i className="fas fa-chart-line text-sm" style={{ color:'#e94560' }}></i>
                                <span className="font-bold text-gray-900 text-sm uppercase tracking-wide">12-Month Amazon Price History</span>
                                <span className="ml-auto text-xs text-gray-400">— green dashed line = your buy price</span>
                            </div>
                            <div className="rounded-xl border border-gray-100 p-4" style={{ background:'#f9fafb' }}>
                                <LineChart />
                                <div className="grid md:grid-cols-3 gap-3 mt-4">
                                    {[
                                        { icon:'fa-arrow-trend-up', color: BLUE,  label:'Amazon Price Range', value:'$121–$162', note:'historically stays well above your buy price' },
                                        { icon:'fa-shopping-cart',  color: GREEN, label:'Your Buy Price',      value: fmt(SAMPLE.buyPrice), note:'locked in — Walmart clearance' },
                                        { icon:'fa-money-bill-wave',color: '#f39c12', label:'Your Profit Margin', value: `${SAMPLE.roi}% ROI`, note:'on every unit sold at list price' },
                                    ].map(stat => (
                                        <div key={stat.label} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100">
                                            <i className={`fas ${stat.icon} mt-0.5 text-sm flex-shrink-0`} style={{ color: stat.color }}></i>
                                            <div>
                                                <div className="text-xs text-gray-500">{stat.label}</div>
                                                <div className="font-bold text-sm" style={{ color: stat.color }}>{stat.value}</div>
                                                <div className="text-xs text-gray-400">{stat.note}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Market stats */}
                            <div className="grid grid-cols-3 gap-4 mt-4">
                                {[
                                    { label:'Amazon BSR',           value:`#${SAMPLE.bsr.toLocaleString()}`,          color: BLUE,   note:'high demand product' },
                                    { label:'Est. Monthly Sales',   value:`${SAMPLE.monthlySales.toLocaleString()}+ units`, color: GREEN,  note:'buyers are waiting' },
                                    { label:'Active Competitors',   value:'0 — ONLY YOU',                              color: ACCENT, note:'exclusive listing' },
                                ].map(stat => (
                                    <div key={stat.label} className="rounded-xl p-4 border border-gray-100 text-center" style={{ background:'white' }}>
                                        <div className="text-xs text-gray-400 mb-1">{stat.label}</div>
                                        <div className="font-extrabold text-sm" style={{ color: stat.color }}>{stat.value}</div>
                                        <div className="text-xs text-gray-400 mt-1">{stat.note}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Report footer */}
                        <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between flex-wrap gap-3" style={{ background:'#fafafa' }}>
                            <span className="text-xs text-gray-400">Sample · Seller Source Arbitrage Report · sellersource.org</span>
                            <Link to="/register"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl font-bold text-white text-xs transition-all hover:opacity-90"
                                style={{ background:'#e94560' }}>
                                <i className="fas fa-file-pdf"></i> Get My Real Report
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── HOW IT WORKS ──────────────────────────────────────────────── */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-5xl mx-auto text-center">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                        style={{ background:'rgba(233,69,96,.1)', color:'#e94560' }}>Process</span>
                    <h2 className="text-3xl font-extrabold mb-3" style={{ color:PRIMARY }}>Simple. Fast. Profitable.</h2>
                    <div className="w-10 h-1 rounded mx-auto mb-4" style={{ background:'#e94560' }}></div>
                    <p className="text-gray-500 max-w-lg mx-auto mb-12">Four steps from sign-up to downloading your exclusive leads.</p>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            { num:1, icon:'fa-user-plus',  title:'Create Account',  desc:'Sign up free in 30 seconds with email or Google.' },
                            { num:2, icon:'fa-credit-card',title:'Choose a Plan',    desc:'Single report for $5.99 or subscribe for unlimited daily exclusives.' },
                            { num:3, icon:'fa-robot',       title:'AI Hunts Deals',  desc:'Our AI scans sold-out clearance across 40+ retailers for your category.' },
                            { num:4, icon:'fa-inbox',       title:'Download & Profit',desc:'Get your PDF, source the stock, list on Amazon — you\'re the only seller.' },
                        ].map(s => (
                            <div key={s.num} className="p-6 rounded-2xl border border-gray-100 bg-gray-50 hover:shadow-lg transition-all hover:-translate-y-1">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-white mx-auto mb-3" style={{ background:PRIMARY }}>
                                    {s.num}
                                </div>
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background:'rgba(233,69,96,.1)' }}>
                                    <i className={`fas ${s.icon} text-sm`} style={{ color:'#e94560' }}></i>
                                </div>
                                <h5 className="font-bold text-gray-900 mb-2">{s.title}</h5>
                                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FEATURES ──────────────────────────────────────────────────── */}
            <section className="py-16 px-4" style={{ background:'#f0f2f8' }}>
                <div className="max-w-6xl mx-auto text-center">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                        style={{ background:'rgba(233,69,96,.1)', color:'#e94560' }}>Features</span>
                    <h2 className="text-3xl font-extrabold mb-3" style={{ color:PRIMARY }}>Everything You Need to Win</h2>
                    <div className="w-10 h-1 rounded mx-auto mb-4" style={{ background:'#e94560' }}></div>
                    <p className="text-gray-500 max-w-lg mx-auto mb-12">Built for serious resellers who want data-backed decisions, not guesswork.</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 text-left">
                        {[
                            { icon:'fa-store-slash',     title:'Out-of-Stock Radar',           desc:'Detects products sold out at Walmart, Target, Best Buy & 37 more retailers — before anyone else knows.' },
                            { icon:'fa-crown',           title:'Zero-Competition Listings',    desc:'Every opportunity is verified to have 0 or near-zero active Amazon sellers at time of report.' },
                            { icon:'fa-calculator',     title:'Full Profit & ROI Analysis',   desc:'Buy price, shipping, FBA fees, net profit, ROI and margin — all calculated per unit, ready to act on.' },
                            { icon:'fa-chart-line',     title:'Price History & Trend Charts', desc:'12-month Amazon price charts show demand consistency so you know your sell price will hold.' },
                            { icon:'fa-link',           title:'Direct Purchase Links',        desc:'One-click links to the clearance product sorted by lowest price — ready to check out immediately.' },
                            { icon:'fa-file-pdf',       title:'Professional PDF Reports',     desc:'Polished, branded PDF with all charts, links and analysis — download instantly from your dashboard.' },
                        ].map(f => (
                            <div key={f.title} className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1 hover:border-red-200">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background:'rgba(233,69,96,.1)' }}>
                                    <i className={`fas ${f.icon} text-lg`} style={{ color:'#e94560' }}></i>
                                </div>
                                <h5 className="font-bold text-gray-900 mb-2">{f.title}</h5>
                                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── URGENCY CTA ───────────────────────────────────────────────── */}
            <section className="py-16 px-4" style={{ background: PRIMARY }}>
                <div className="max-w-3xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-6"
                        style={{ background:'rgba(233,69,96,.25)', border:'1px solid rgba(233,69,96,.5)', color:'#ff9fba' }}>
                        <i className="fas fa-fire-flame-curved"></i> Limited Window — Stock Won't Last
                    </div>
                    <h2 className="text-3xl font-extrabold text-white mb-4">
                        Clearance Stock Disappears Fast.<br />
                        <span style={{ color:'#e94560' }}>Purchase Today to Lock In Your Edge.</span>
                    </h2>
                    <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                        Out-of-stock windows close quickly. Once other sellers notice the opportunity or the retailer restocks, your exclusive advantage is gone.
                        Every hour you wait is profit left on the table.
                    </p>

                    {/* Live countdown */}
                    <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl mb-8"
                        style={{ background:'rgba(233,69,96,.15)', border:'1px solid rgba(233,69,96,.3)' }}>
                        <i className="fas fa-clock text-red-400 text-lg"></i>
                        <div className="text-left">
                            <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Today's Reports Expire In</div>
                            <Urgency />
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 justify-center mb-6">
                        <Link to="/register"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90"
                            style={{ background:'#e94560' }}>
                            <i className="fas fa-bolt"></i> Get My Exclusive Report Now
                        </Link>
                        <Link to="/pricing"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-sm text-white"
                            style={{ background:'rgba(255,255,255,.08)', border:'1.5px solid rgba(255,255,255,.2)' }}>
                            View All Plans
                        </Link>
                    </div>
                    <p className="text-gray-500 text-xs">No subscription required · Pay once · Download instantly · Full refund if unsatisfied</p>
                </div>
            </section>

            {/* ── REVIEWS ───────────────────────────────────────────────────── */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                            style={{ background:'rgba(233,69,96,.1)', color:'#e94560' }}>Real Results</span>
                        <h2 className="text-3xl font-extrabold mb-3" style={{ color:PRIMARY }}>
                            What Our Sellers Are Saying
                        </h2>
                        <div className="w-10 h-1 rounded mx-auto mb-4" style={{ background:'#e94560' }}></div>
                        <div className="flex items-center justify-center gap-2">
                            {[1,2,3,4,5].map(i => (
                                <i key={i} className="fas fa-star text-yellow-400 text-sm"></i>
                            ))}
                            <span className="ml-2 text-sm font-semibold text-gray-600">4.9 / 5 average from 200+ sellers</span>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                name:   'Sarah M.',
                                role:   'Amazon FBA Seller',
                                img:    'https://i.pravatar.cc/80?img=47',
                                stars:  5,
                                half:   false,
                                quote:  '"My very first report found a product with $47 profit per unit. I was literally the only seller on Amazon for two weeks straight. Zero competition, full price control. Absolutely worth every penny."',
                                detail: 'Electronics · $1,200+ first month profit',
                            },
                            {
                                name:   'Marcus R.',
                                role:   'Full-Time Reseller',
                                img:    'https://i.pravatar.cc/80?img=12',
                                stars:  5,
                                half:   false,
                                quote:  '"I tripled my Amazon income in under 60 days using Seller Source. The out-of-stock angle is genius — no price wars, no undercutting, just pure profit. The price history charts alone are worth the subscription."',
                                detail: 'Home & Garden · 3× revenue increase',
                            },
                            {
                                name:   'Priya K.',
                                role:   'Part-Time Side Hustler',
                                img:    'https://i.pravatar.cc/80?img=31',
                                stars:  4,
                                half:   true,
                                quote:  '"The charts and breakdown make it so easy to understand exactly where my money goes and how much I\'ll make back. I do wish there were more reports per day on the basic plan, but the quality is really impressive."',
                                detail: 'Baby & Kids · $640 profit on first order',
                            },
                            {
                                name:   'James T.',
                                role:   'Amazon Wholesale Seller',
                                img:    'https://i.pravatar.cc/80?img=68',
                                stars:  5,
                                half:   false,
                                quote:  '"The direct purchase links already sorted by lowest price save me so much time. I click, I buy, I ship to FBA, I list — and because the product is out of stock everywhere else, buyers come straight to me. Brilliant system."',
                                detail: 'Sports & Outdoors · 58% avg ROI',
                            },
                        ].map(r => (
                            <div key={r.name}
                                className="flex flex-col rounded-2xl p-6 border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1"
                                style={{ background:'#fafbff' }}>
                                {/* Stars */}
                                <div className="flex items-center gap-0.5 mb-4">
                                    {[1,2,3,4].map(i => (
                                        <i key={i} className="fas fa-star text-yellow-400 text-sm"></i>
                                    ))}
                                    {r.half
                                        ? <i className="fas fa-star-half-alt text-yellow-400 text-sm"></i>
                                        : <i className="fas fa-star text-yellow-400 text-sm"></i>
                                    }
                                    <span className="ml-1.5 text-xs font-bold text-gray-500">{r.half ? '4.5' : '5.0'}</span>
                                </div>

                                {/* Quote */}
                                <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-5 italic">{r.quote}</p>

                                {/* Result badge */}
                                <div className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 self-start"
                                    style={{ background:'rgba(39,174,96,.1)', color:GREEN }}>
                                    <i className="fas fa-chart-line text-xs"></i>
                                    {r.detail}
                                </div>

                                {/* Reviewer */}
                                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                                    <img
                                        src={r.img}
                                        alt={r.name}
                                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                        style={{ border:'2px solid #e94560' }}
                                    />
                                    <div>
                                        <div className="font-bold text-sm text-gray-900">{r.name}</div>
                                        <div className="text-xs text-gray-400">{r.role}</div>
                                    </div>
                                    <i className="fas fa-check-circle ml-auto text-sm" style={{ color:GREEN }} title="Verified Buyer"></i>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
